// ----------------------------------------------------------------------------
// Component: Accordion
// ----------------------------------------------------------------------------
//
// Generic accordion component.
//
// Requires the following structure:
// .js-accordion__item
//     .js-accordion__button
//     .js-accordion__content
//
// When the button is clicked, 'is-open' class is added/removed for all three
// elements.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './accordion.scss';

// -------------------------------------
// Variables
// -------------------------------------

const ACCORDION = '.js-accordion';
const ACCORDION_BUTTON = '.js-accordion__button';
const ACCORDION_ITEM = '.js-accordion__item';
const ACCORDION_CONTENT = '.js-accordion__content';
const ACCORDION_TITLE = '.accordion__title';

// Constant trigger classes
const OPEN_CLASS = 'is-open';

// -------------------------------------
// Helper Functions
// -------------------------------------

function setTabIndex(e, value) {
    e.setAttribute('tabindex', value);
}

function toggleAccordion(accordion) {
    // Close all other accordions
    accordion.item.parentElement
        .querySelectorAll('.' + OPEN_CLASS)
        .forEach(function (e) {
            e.classList.remove(OPEN_CLASS);
        });

    accordion.item.parentElement
        .querySelectorAll(ACCORDION_CONTENT)
        .forEach(function (e) {
            e.style.height = '0';
            // Use setTimeout to allow transition to complete before hiding
            setTimeout(function() {
                e.hidden = true;
            }, 350); // Match transition duration
        });

    accordion.item.parentElement
        .querySelectorAll(ACCORDION_BUTTON)
        .forEach(function (e) {
            e.setAttribute('aria-expanded', 'false');
        });

    accordion.item.parentElement
        .querySelectorAll(ACCORDION_CONTENT)
        .forEach(function (e) {
            e.querySelectorAll('a').forEach((el) => {
                setTabIndex(el, -1);
            });
        });

    if (accordion.isOpening) {
        // Add classes and remove hidden first
        accordion.button.classList.add(OPEN_CLASS);
        accordion.item.classList.add(OPEN_CLASS);
        accordion.content.classList.add(OPEN_CLASS);
        accordion.content.hidden = false;

        // Set initial height to 0 for animation
        accordion.content.style.height = '0';

        // Use double requestAnimationFrame to ensure styles are fully applied before measuring
        requestAnimationFrame(function() {
            requestAnimationFrame(function() {
                // Now calculate scrollHeight with the is-open class applied (includes padding)
                accordion.content.style.height = accordion.content.scrollHeight + 'px';
            });
        });

        accordion.button.setAttribute('aria-expanded', 'true');
        accordion.content.querySelectorAll('a').forEach(function (el) {
            setTabIndex(el, 0);
        });
    }
}

function accordionEventHandle(e, button) {
    const accordion = {
        button: button,
        item: button.closest(ACCORDION_ITEM),
        title: button.closest(ACCORDION_TITLE),
        content: button.closest(ACCORDION_TITLE).nextElementSibling,
        isOpening: !button.classList.contains(OPEN_CLASS),
        container: button.closest(ACCORDION),
        button_group: button
            .closest(ACCORDION)
            .querySelectorAll(ACCORDION_BUTTON),
    };

    if (e.type === 'click') {
        e.preventDefault();
        toggleAccordion(accordion);
    } else if (e.type === 'keydown') {
        const key = e.which.toString();
        if (key.match(/35|36|38|40/)) {
            e.preventDefault();
            const index = Array.prototype.indexOf.call(
                accordion.button_group,
                button,
            );
            const length = accordion.button_group.length;
            let newIndex;
            switch (key) {
                case '38': // Up
                    newIndex = (index + length + -1) % length;
                    accordion.button_group[newIndex].focus();
                    break;
                case '40': // Down
                    newIndex = (index + length + 1) % length;
                    accordion.button_group[newIndex].focus();
                    break;
                case '35': // End
                    accordion.button_group[length - 1].focus();
                    break;
                case '36': // Home
                    accordion.button_group[0].focus();
                    break;
            }
        }
    }
}

function initAccordionTabIndex() {
    const accordionContentLinks = document.querySelectorAll(
        ACCORDION_CONTENT + ' a',
    );
    accordionContentLinks.forEach(function (el) {
        setTabIndex(el, -1);
    });
}

// -------------------------------------
// Hooks (event delegation so accordions work after section re-render)
// -------------------------------------

document.addEventListener('click', function (e) {
    const button = e.target.closest(ACCORDION_BUTTON);
    if (!button) return;
    accordionEventHandle(e, button);
});

document.addEventListener('keydown', function (e) {
    const button = e.target.closest(ACCORDION_BUTTON);
    if (!button) return;
    accordionEventHandle(e, button);
});

document.addEventListener('DOMContentLoaded', initAccordionTabIndex);

window.addEventListener('resize', function () {
    // Re-adjust all the expanded accordion content height on "resize" event
    const expandedAccordionContent = document.querySelectorAll(
        `${ACCORDION_CONTENT}.${OPEN_CLASS}`,
    );
    expandedAccordionContent.forEach(function (accordionContent) {
        accordionContent.style.height = accordionContent.scrollHeight + 'px';
    });
});
